import { useEffect, useRef, useState } from 'react';
import usePrefersReducedMotion from './usePrefersReducedMotion';

const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

/**
 * Counts from 0 up to `target` once the value arrives. Returns null while the
 * target is still unknown so callers can render a skeleton instead of a zero.
 * Honours prefers-reduced-motion by jumping straight to the value.
 */
export default function useCountUp(target, duration = 400) {
  const [value, setValue] = useState(null);
  const prefersReduced = usePrefersReducedMotion();
  const frame = useRef(null);

  useEffect(() => {
    if (typeof target !== 'number' || Number.isNaN(target)) {
      setValue(null);
      return undefined;
    }

    if (prefersReduced) {
      setValue(target);
      return undefined;
    }

    const start = performance.now();
    const tick = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      setValue(Math.round(target * easeOutCubic(progress)));
      if (progress < 1) frame.current = requestAnimationFrame(tick);
    };
    frame.current = requestAnimationFrame(tick);

    return () => {
      if (frame.current) cancelAnimationFrame(frame.current);
    };
  }, [target, duration, prefersReduced]);

  return value;
}
